import type {
  DropDownNoSelection,
  DropDownResult,
  DynamicFieldComponentProps,
  DynamicFormButtonSetting,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import { DataSearchLocation, OdinDataSender } from '@myosh/odin-components';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useUserAccess } from '../../../hooks/use-user-access';
import { useFormSettingsQuery } from '../../../redux/services/api';
import { useGetCompetencyFieldMappingQuery, useLazyTrainingFieldOptionsQuery } from '../../../redux/services/field';
import { isWebView } from '../../../redux/util/webview-base-query';
import { toDropDownResult } from '../../../utils/data-transformation-utils';
import OdinDropDown from '../custom-drop-downs/odin-drop-down.component';
import { TrainingManagementPermissions } from '../../../utils/user.permissions';

export interface TrainingFieldProps {
  formId: number;
  fieldId: number;
  setFieldValue?: (name: string, value: unknown) => void;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
}

const trainingFieldPermissions = {
  MODIFY: [
    TrainingManagementPermissions.TRAINING_MANAGEMENT_READ,
    TrainingManagementPermissions.TRAINING_MANAGEMENT_MODIFY,
    TrainingManagementPermissions.TRAINING_MANAGEMENT_CREATE,
  ],
};

export default function TrainingField({
  label,
  value,
  onChange,
  readOnly,
  required,
  error,
  inputRef,
  formId,
  fieldId,
  setFieldValue,
  setFormButtonsState,
}: DynamicFieldComponentProps<DropDownResult | DropDownNoSelection> & TrainingFieldProps) {
  const [competencyId, setCompetencyId] = useState<number | null>();
  const [trainingFieldOptions] = useLazyTrainingFieldOptionsQuery();

  const { editAccess: hasTrainingManagementAdminAccess } = useUserAccess({
    permissions: trainingFieldPermissions,
    mustMatchAll: true,
  });

  const { data: formSettings } = useFormSettingsQuery(formId ?? 0, {
    skip: formId === undefined || formId === 0,
  });

  const { data: competencyFieldMapping, isFetching } = useGetCompetencyFieldMappingQuery({ formId, competencyId });

  useEffect(() => {
    if (!readOnly) {
      setFormButtonsState?.((prev) =>
        prev.map((button) => ({
          ...button,
          disabled: isFetching,
        }))
      );
    }
  }, [isFetching, readOnly, setFormButtonsState]);

  useEffect(() => {
    // allow if check to pass if competencyId is a number or null when deselecting a value
    if (
      competencyId !== undefined &&
      !isFetching &&
      competencyFieldMapping &&
      competencyFieldMapping.competencyFieldMappingDTO &&
      formSettings
    ) {
      for (let i = 0, length = competencyFieldMapping.competencyFieldMappingDTO.length; i < length; i++) {
        const { targetFieldId, competencyFieldValue } = competencyFieldMapping.competencyFieldMappingDTO[i];
        let finalValue = cloneDeep(competencyFieldValue);
        if (Array.isArray(finalValue)) {
          for (let j = 0; j < finalValue.length; j++) {
            finalValue[j] = toDropDownResult(finalValue[j]);
          }
        } else if (typeof finalValue === 'object') {
          finalValue = toDropDownResult(finalValue);
        }

        setFieldValue?.(String(targetFieldId), finalValue ?? null);
      }
    }
  }, [competencyId, isFetching, competencyFieldMapping, formSettings]);

  const getOptionsData = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (options && options.page && options.pageSize) {
        try {
          if (options.fieldFilters?.text) {
            options.fieldFilters['name'] = options.fieldFilters.text;
            delete options.fieldFilters.text;
          }
          const inWebView = isWebView();
          const result = await trainingFieldOptions({
            page: inWebView ? 1 : options.page,
            pageSize: inWebView ? -1 : options.pageSize,
            filters: { ...options.fieldFilters, archived: { value: false, comparison: '=' } },
            enforceHierarchyRestriction: inWebView || hasTrainingManagementAdminAccess,
          }).unwrap();

          const data = result.map((item) => {
            return {
              renewalInterval: item.renewalInterval,
              onlineLearning: item.onlineLearning,
              value: item.id,
              text: item.name,
            };
          });

          subscriber.sendData({ data, requestId: options.requestId });
        } catch (_) {
          subscriber.sendData();
        }
      }
    },
  };

  const onValueChange = (fieldValue: DropDownResult | DropDownNoSelection) => {
    if (onChange && !!fieldValue) {
      onChange(fieldValue);
      if (
        Number(fieldId) === competencyFieldMapping?.trainingFieldId &&
        competencyFieldMapping?.competencyFieldMappingDTO
      ) {
        setCompetencyId((fieldValue as DropDownResult).value as number | null);
      }
    }
  };

  return (
    <OdinDropDown
      label={label}
      textField="text"
      valueField="value"
      value={value}
      onChange={(value) => onValueChange(value as DropDownResult | DropDownNoSelection)}
      readOnly={readOnly}
      required={required}
      allowSearch={true}
      error={error}
      data={getOptionsData}
      inputRef={inputRef}
      deselectValue={undefined}
      filterLocation={DataSearchLocation.Api}
    />
  );
}
